import { formatDate, formatUTCDate } from 'src/helpers/date';
import { generateAdditionalData, generateMediaForSchema, serializePostFromData } from 'src/helpers/socialMediaHelpers';
import INTEGRATION_CHANNEL_TYPES from 'src/enums/integrationChannelTypes';
import POST_MEDIA_TYPES from 'src/enums/postMediaTypes';
import POST_STATUSES from 'src/enums/postStatuses';
import { $axios } from 'boot/axios';
import dayjs from 'dayjs';
import statusesDropdown from './statusesDropdown';
import channelsDropdown from './channelsDropdown';
import generateImage from './generateImage';
import generateText from './generateText';

const defaultPostForm = () => ({
  content: '',
  media: [],
  type: POST_MEDIA_TYPES.POST,
  status: undefined,
  publishAtUTC: undefined,
  publishAt: undefined,
  generation: undefined,
  generationGuid: undefined,
  date: undefined,
  time: undefined,
  templateType: undefined,
  link: undefined,
  title: undefined,
  privacy: undefined,
  disableComments: true,
  disableDuet: true,
  disableStitch: true,
  isAIGC: false,
  brandContentToggle: false,
  brandOrganicToggle: false,
  notifySubscribers: true,
  embeddable: true,
  selfDeclaredMadeForKids: false,
  categoryId: undefined,
  privacyStatus: undefined,
  license: undefined,
});

const state = () => ({
  currentChannel: undefined,
  tikTokChannelGuid: undefined,
  tikTopDefaultOptions: {},
  tikTokMaxVideoDuration: {},
  currentPost: defaultPostForm(),
  newPostNetworks: [],
  newPosts: {},
  channels: [],
  posts: [],
  templates: {},
  hasValidationErrors: {},
  boards: {},
  privacyOptions: {},
});

const smpGetters = {
  // TODO: Replace to funki account
  // dateInCurrentTimeZone: (state, getters, rootState) => formatDate(state.currentPost.publishAt, 'MMMM DD', rootState.app.account.currentLanguage),
  dateInCurrentTimeZone: (state) => formatDate(state.currentPost.publishAt, 'MMMM DD', 'en'),
  timeInCurrentTimeZone: (state) => formatDate(state.currentPost.publishAt, 'HH:mm'),
  publishAtUtc: (state) => formatUTCDate(state.currentPost.publishAt, 'YYYY-MM-DDTHH:mm:ss'),
  hasPostForDifferentNetworks: (state) => Object.keys(state.newPosts).length > 1,
  maxVideoDuration: (state) => state.tikTokMaxVideoDuration[state.tikTokChannelGuid],
};

const mutations = {
  setChannels(state, payload) {
    state.channels = payload;
  },
  addChannel(state, payload) {
    state.channels.push(payload);
  },
  setCurrentChannel(state, payload) {
    state.currentChannel = payload;
  },
  setPosts(state, payload) {
    state.posts = payload;
  },
  addPost(state, payload) {
    state.posts.push(payload);
  },
  setPost(state, payload) {
    state.currentPost = payload;
  },
  setPostContent(state, payload) {
    state.currentPost.content = payload;
  },
  setPostMedia(state, payload) {
    state.currentPost.media = payload;
  },
  setVideoCoverImage(state, payload) {
    const video = state.currentPost.media.find((item) => item.url === payload.url);
    if (video) {
      video.coverImage = payload.coverImage;
    }
  },
  addPostMedia(state, payload) {
    const mediaArray = Array.isArray(payload) ? payload : [payload];
    state.currentPost.media = [...new Set([...state.currentPost.media, ...mediaArray])];
  },
  setPublishAt(state, payload) {
    state.currentPost.publishAt = payload;
  },
  setPostLink(state, payload) {
    state.currentPost.link = payload;
  },
  setTikTokChannelGuid(state, payload) {
    state.tikTokChannelGuid = payload;
  },
  setPostTitle(state, payload) {
    state.currentPost.title = payload;
  },
  setPublishAtForAllNewPosts(state, payload) {
    Object.keys(state.newPosts)
      .forEach((networkKey) => {
        state.newPosts[networkKey].publishAt = payload;
      });
  },
  setPostGeneration(state, payload) {
    state.currentPost.generation = payload;
    state.currentPost.generationGuid = payload.guid;
  },
  setPostTemplateType(state, payload) {
    state.currentPost.templateType = payload;
  },
  setNewPostNetworks(state, payload) {
    state.newPostNetworks = payload;
  },
  setNewPosts(state, payload) {
    state.newPosts = payload;
  },
  addPostForNetwork(state, {
    key,
    value,
  }) {
    state.newPosts[key] = value;
  },
  setTemplates(state, {
    key,
    value,
  }) {
    state.templates[key] = value;
  },
  setHasValidationErrors(state, {
    key,
    value,
  }) {
    state.hasValidationErrors[key] = value;
  },
  setBoards(state, {
    key,
    value,
  }) {
    state.boards[key] = value;
  },
  setPrivacyOptions(state, {
    key,
    value,
  }) {
    state.privacyOptions[key] = value;
  },
  setTikTokMaxVideoDuration(state, {
    key,
    value,
  }) {
    state.tikTokMaxVideoDuration[key] = value;
  },
  setChannelBoard(state, payload) {
    const channel = state.channels.find((item) => item.guid === payload.guid);
    channel.board = payload.board;
  },
  setPostMediaType(state, payload) {
    state.currentPost.type = payload;
  },
  setTikTopDefaultOptions(state, { key, value }) {
    state.tikTopDefaultOptions[key] = value;
  },
};

const actions = {
  async fetchTemplate(_, { type }) {
    const { data } = await $axios.get(`generator/templates/${type}`);
    return data.data;
  },
  async getAllChannels({ commit }) {
    const { data } = (await $axios.get('generator/social-media-planner/channels')).data;
    const mappedChannels = data.map((channel) => {
      if (channel.template.type === INTEGRATION_CHANNEL_TYPES.PINTEREST) {
        return {
          ...channel,
          board: undefined,
        };
      }
      return channel;
    });
    commit('setChannels', mappedChannels);
    commit('channelsDropdown/setChannels', mappedChannels);
  },
  async createChannel({ commit }, type) {
    const { data } = (await $axios.post('generator/social-media-planner/channels', { type })).data;
    commit('addChannel', data);
    return data;
  },
  async deleteChannel({
    state,
    commit,
  }, guid) {
    await $axios.post(`generator/social-media-planner/channels/${guid}`);

    const updatedChannels = state.channels.filter((channel) => channel.guid !== guid);
    commit('setChannels', updatedChannels);
  },
  async getPostsWithParams({ commit }, params) {
    try {
      const { data } = (await $axios.get('generator/social-media-planner/channels/posts', { params })).data;
      commit('setPosts', data.map((post) => serializePostFromData(post)));
    } catch (error) {
      console.error(error);
    }
  },
  async getPostByGuid({ commit }, {
    channelGuid,
    postGuid,
  }) {
    const { data } = (await $axios(`generator/social-media-planner/channels/${channelGuid}/posts/${postGuid}`)).data;
    commit('setPost', serializePostFromData(data));
    commit('setCurrentChannel', data.socialMediaPlannerChannel);
  },
  async createPost({
    commit,
    state,
  }, {
    channelGuid,
    post,
    status = POST_STATUSES.DRAFT,
  }) {
    if (status === POST_STATUSES.DRAFT) {
      const publishAt = dayjs(post.publishAt);
      const diff = publishAt.diff(dayjs(), 'minute');
      if (diff < 0) {
        post.publishAt = dayjs()
          .add(1, 'minute')
          .format('YYYY-MM-DDTHH:mm:ss');
      }
    }

    const channel = state.channels.find((item) => item.guid === channelGuid);
    let { content } = post;
    if (channel.template.type === INTEGRATION_CHANNEL_TYPES.INSTAGRAM) {
      content = post.type === POST_MEDIA_TYPES.STORIES ? '' : post.content;
    }

    let posts = [
      {
        publishAtUTC: formatUTCDate(post.publishAt, 'YYYY-MM-DDTHH:mm:ss'),
        templateType: post.templateType,
        generationGuid: post.generationGuid,
        status,
        data: {
          content,
          media: generateMediaForSchema(post),
          additionalData: generateAdditionalData(post, channel),
        },
      },
    ];
    if (post.additionalThreads?.length) {
      const additionalThreads = post.additionalThreads.map((thread) => (
        {
          publishAtUTC: formatUTCDate(post.publishAt, 'YYYY-MM-DDTHH:mm:ss'),
          templateType: thread.templateType,
          generationGuid: thread.generationGuid,
          status,
          data: {
            content: thread.content,
            media: generateMediaForSchema(thread),
            additionalData: generateAdditionalData(thread, channel),
          },
        }
      ));

      posts = [...posts, ...additionalThreads];
    }
    const { data } = (await $axios.post(`generator/social-media-planner/channels/${channelGuid}/posts`, {
      posts,
    }, {
      skipReplaceInterceptor: true,
    })).data;

    const serializedPost = serializePostFromData(data[0]);

    commit('setPost', serializedPost);
    commit('addPost', serializedPost);

    return serializedPost;
  },
  resetPost({ commit }) {
    commit('setPost', defaultPostForm());
  },
  async updatePost({
    state,
    getters,
    commit,
  }, status) {
    let publishAtUTC = getters.publishAtUtc;

    if (status === POST_STATUSES.DRAFT) {
      const publishAt = dayjs(publishAtUTC);
      const diff = publishAt.diff(dayjs(), 'minute');
      if (diff < 0) {
        publishAtUTC = dayjs()
          .add(1, 'minute')
          .format('YYYY-MM-DDTHH:mm:ss');
      }
    }

    const channel = state.channels.find((item) => item.guid === state.currentChannel.guid);
    const { data } = (await $axios.put(`generator/social-media-planner/channels/${state.currentChannel.guid}/posts/${state.currentPost.guid}`, {
      publishAtUTC,
      templateType: state.currentPost.socialMediaPlannerChannel.template.type,
      status,
      generationGuid: state.currentPost.generationGuid,
      data: {
        content: state.currentPost.content,
        media: generateMediaForSchema(state.currentPost),
        additionalData: generateAdditionalData(state.currentPost, channel),
      },
      childPosts: state.currentPost.childPosts,
    }, {
      skipReplaceInterceptor: true,
    })).data;

    const serializedPost = serializePostFromData(data);
    const updatedPosts = state.posts.map((post) => {
      if (post.guid === serializedPost.guid) {
        return serializedPost;
      }
      return post;
    });

    commit('setPost', serializedPost);
    commit('setPosts', updatedPosts);
  },
  async deletePost({
    commit,
    state,
    dispatch,
  }, {
    postGuid,
    channelGuid,
  }) {
    await $axios.delete(`generator/social-media-planner/channels/${channelGuid}/posts/${postGuid}`);
    if (state.currentPost.guid === postGuid) {
      dispatch('resetPost');
    }
    const filteredPosts = state.posts.filter((post) => post.guid !== postGuid);
    commit('setPosts', filteredPosts);
  },
  clearPostData({ commit }) {
    commit('setCurrentChannel', undefined);
    commit('setPost', defaultPostForm());
    commit('setNewPostNetworks', []);
    commit('setNewPosts', {});
    commit('setHasValidationErrors', {});
  },
  async getBoards({ commit }, channelGuid) {
    const { data } = (await $axios.get(`generator/social-media-planner/channels/${channelGuid}/external-data?type=boards`)).data;
    commit('setBoards', {
      key: channelGuid,
      value: data,
    });
    return data;
  },
  async getPrivacyOptions({ commit }, channelGuid) {
    const { data } = (await $axios.get(`generator/social-media-planner/channels/${channelGuid}/external-data?type=creatorInfo`)).data;

    if (!data[0]) {
      return;
    }

    commit('setPrivacyOptions', {
      key: channelGuid,
      value: data[0].privacyOptions,
    });

    commit('setTikTokMaxVideoDuration', {
      key: channelGuid,
      value: data[0].maxVideoPostDurationSec,
    });

    commit('setTikTopDefaultOptions', {
      key: channelGuid,
      value: {
        disableComments: data[0].disableComments,
        disableDuet: data[0].disableDuet,
        disableStitch: data[0].disableStitch,
      },
    });
    return data;
  },
};

export default {
  namespaced: true,
  modules: {
    generateText,
    generateImage,
    channelsDropdown,
    statusesDropdown,
  },
  state,
  mutations,
  actions,
  getters: smpGetters,
};
